import React, { useState, useTransition } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useLocalAuth } from '../../contexts/LocalAuthContext';

/**
 * Local-auth sign-in screen styled with the same lg-* design language as
 * the template's main login-interface (dark left brand panel + light right
 * form panel). All CSS is scoped inside the component to avoid leaking.
 */
const LocalLoginPage: React.FC = () => {
  const { login } = useLocalAuth();
  const navigate = useNavigate();
  const location = useLocation() as { state?: { from?: string } };
  const redirectTo = location.state?.from || '/home';

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!username.trim() || !password) {
      setError('Username and password are required.');
      return;
    }
    startTransition(async () => {
      try {
        await login(username.trim(), password);
        navigate(redirectTo, { replace: true });
      } catch (err: any) {
        setError(err?.response?.data?.message || err.message || 'Sign-in failed.');
      }
    });
  };

  return (
    <div className="lg-root">
      <style>{`
        .lg-root {
          display: flex;
          height: 100vh;
          overflow: hidden;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", Roboto, sans-serif;
          --lg-dark: #0f172a;
          --lg-mid: #1e293b;
          --lg-teal: #14b8a6;
          --lg-teal-focus: rgba(20, 184, 166, 0.15);
          --lg-slate-50: #f8fafc;
          --lg-slate-100: #f1f5f9;
          --lg-slate-200: #e2e8f0;
          --lg-slate-400: #94a3b8;
          --lg-slate-500: #64748b;
          --lg-slate-700: #334155;
          --lg-slate-900: #0f172a;
          --lg-danger: #ef4444;
          --lg-danger-bg: #fef2f2;
          --lg-danger-border: #fecaca;
        }
        .lg-root * { box-sizing: border-box; }

        .lg-brand {
          width: 44%;
          flex-shrink: 0;
          background: linear-gradient(155deg, var(--lg-dark) 0%, var(--lg-mid) 100%);
          display: flex; flex-direction: column;
          padding: 2.5rem 3rem;
          position: relative; overflow: hidden;
          box-shadow: 8px 0 40px rgba(0,0,0,0.3);
          z-index: 1;
        }
        .lg-brand-logo { display:flex; align-items:center; flex-shrink:0; z-index:1; }
        .lg-graphic {
          flex: 1;
          display: flex; align-items: center; justify-content: center;
          padding: 2rem 0;
        }
        .lg-graphic svg { width:100%; max-width:340px; height:auto; }
        .lg-hub-ring   { animation: lg-ring-pulse 4s ease-in-out infinite; }
        .lg-hub-ring-2 { animation: lg-ring-pulse 4s ease-in-out infinite 1.5s; }
        .lg-hub-ring-3 { animation: lg-ring-pulse 4s ease-in-out infinite 0.8s; }
        @keyframes lg-ring-pulse { 0%,100% { opacity: 0.15; } 50% { opacity: 0.55; } }
        .lg-taglines { z-index:1; flex-shrink:0; }
        .lg-tagline-h {
          font-size: 1.75rem; font-weight: 700; color: #fff;
          line-height: 1.2; letter-spacing: -0.035em; margin: 0 0 0.625rem;
        }
        .lg-tagline-p { font-size: 0.875rem; color: rgba(255,255,255,0.38); margin: 0; }
        .lg-features {
          margin-top: 1.5rem;
          display: flex; flex-direction: column; gap: 0.625rem;
        }
        .lg-feature-item {
          display: flex; align-items:center; gap: 0.75rem;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 0.625rem; padding: 0.625rem 0.75rem;
        }
        .lg-feature-icon {
          width: 2rem; height: 2rem; border-radius: 0.5rem;
          background: rgba(20,184,166,0.12);
          border: 1px solid rgba(20,184,166,0.2);
          display:flex; align-items:center; justify-content:center;
          color: var(--lg-teal); flex-shrink:0;
        }
        .lg-feature-text { display:flex; flex-direction:column; gap: 0.125rem; }
        .lg-feature-title { font-size: 0.8125rem; font-weight: 600; color: rgba(255,255,255,0.82); line-height: 1.3; }
        .lg-feature-desc  { font-size: 0.6875rem; color: rgba(255,255,255,0.35); line-height: 1.4; }
        .lg-brand-footer  { margin-top: 2rem; flex-shrink: 0; }
        .lg-version       { font-size: 0.6875rem; color: rgba(255,255,255,0.18); font-weight: 500; letter-spacing: 0.03em; }

        .lg-form-panel {
          flex: 1;
          background: var(--lg-slate-50);
          background-image:
            linear-gradient(rgba(15,23,42,0.025) 1px, transparent 1px),
            linear-gradient(90deg, rgba(15,23,42,0.025) 1px, transparent 1px);
          background-size: 32px 32px;
          display: flex; align-items: center; justify-content: center;
          padding: 2.5rem 1.5rem; overflow-y: auto;
        }
        .lg-form-inner { width: 100%; max-width: 400px; animation: lg-enter 0.5s cubic-bezier(0.16,1,0.3,1); }
        @keyframes lg-enter { 0% { opacity: 0; transform: translateY(8px); } 100% { opacity: 1; transform: translateY(0); } }

        .lg-app-identity { display:flex; align-items:center; gap: 0.75rem; margin-bottom: 1.5rem; }
        .lg-app-badge {
          width: 2.25rem; height: 2.25rem; border-radius: 0.5rem;
          background: var(--lg-slate-900); color: #fff;
          display:flex; align-items:center; justify-content:center;
          font-weight: 700; font-size: 0.8125rem;
        }
        .lg-app-name { font-size: 0.9375rem; font-weight: 700; color: var(--lg-slate-900); }

        .lg-form-h1 { font-size: 1.625rem; font-weight: 700; color: var(--lg-slate-900); letter-spacing: -0.02em; margin: 0 0 0.375rem; }
        .lg-form-sub { font-size: 0.8125rem; color: var(--lg-slate-500); margin: 0 0 1.75rem; }

        .lg-field { margin-bottom: 1rem; }
        .lg-label-row { display:flex; align-items:center; justify-content:space-between; margin-bottom: 0.375rem; }
        .lg-label { font-size: 0.75rem; font-weight: 600; color: var(--lg-slate-700); letter-spacing: 0.01em; }
        .lg-input-wrap { position: relative; }
        .lg-input-icon {
          position: absolute; left: 0.75rem; top: 50%; transform: translateY(-50%);
          color: var(--lg-slate-400); display:flex; align-items:center;
        }
        .lg-input {
          width: 100%; height: 2.625rem;
          padding: 0 0.875rem 0 2.5rem;
          font-size: 0.875rem; color: var(--lg-slate-900);
          background: #fff; border: 1px solid var(--lg-slate-200);
          border-radius: 0.5rem; outline: none;
          transition: all 0.18s ease;
        }
        .lg-input:focus { border-color: var(--lg-teal); box-shadow: 0 0 0 3px var(--lg-teal-focus); }
        .lg-input-pw { padding-right: 2.5rem; }
        .lg-pw-toggle {
          position: absolute; right: 0.75rem; top: 50%; transform: translateY(-50%);
          background: none; border: none; color: var(--lg-slate-400);
          cursor: pointer; padding: 0.25rem; display:flex; align-items:center;
        }
        .lg-pw-toggle:hover { color: var(--lg-slate-700); }

        .lg-error-alert {
          display: flex; align-items: center; gap: 0.625rem;
          background: var(--lg-danger-bg); border: 1px solid var(--lg-danger-border);
          color: var(--lg-danger); padding: 0.625rem 0.875rem;
          border-radius: 0.5rem; margin-bottom: 1rem; font-size: 0.8125rem;
        }

        .lg-submit {
          width: 100%; height: 2.75rem; margin-top: 0.5rem;
          background: var(--lg-teal); color: #fff;
          border: none; border-radius: 0.5rem;
          font-size: 0.875rem; font-weight: 600;
          cursor: pointer; transition: all 0.18s ease;
          display:flex; align-items:center; justify-content:center; gap: 0.5rem;
        }
        .lg-submit:hover:not(:disabled) { background: #0f9b8e; box-shadow: 0 4px 14px rgba(20,184,166,0.3); }
        .lg-submit:disabled { opacity: 0.55; cursor: not-allowed; }
        .lg-spinner { width: 1rem; height: 1rem; border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; animation: lg-spin 0.75s linear infinite; }
        @keyframes lg-spin { to { transform: rotate(360deg); } }

        .lg-form-footer {
          margin-top: 1.5rem; padding-top: 1.5rem;
          border-top: 1px solid var(--lg-slate-200);
          text-align: center; font-size: 0.8125rem; color: var(--lg-slate-500);
        }
        .lg-form-footer a { color: var(--lg-teal); font-weight: 600; text-decoration: none; }
        .lg-form-footer a:hover { text-decoration: underline; }

        @media (max-width: 900px) { .lg-brand { display: none; } }
      `}</style>

      <aside className="lg-brand">
        <div className="lg-brand-logo">
          <div style={{ display:'flex', alignItems:'center', gap:'0.625rem' }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background:'var(--lg-teal)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:800 }}>TT</div>
            <span style={{ color: '#fff', fontSize: '1rem', fontWeight: 700, letterSpacing: '-0.01em' }}>Time Tracker</span>
          </div>
        </div>

        <div className="lg-graphic">
          <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle className="lg-hub-ring"   cx="120" cy="120" r="100" stroke="rgba(20,184,166,0.35)" strokeWidth="1" />
            <circle className="lg-hub-ring-2" cx="120" cy="120" r="70"  stroke="rgba(20,184,166,0.4)"  strokeWidth="1" />
            <circle className="lg-hub-ring-3" cx="120" cy="120" r="40"  stroke="rgba(20,184,166,0.55)" strokeWidth="1" />
            <circle cx="120" cy="120" r="12" fill="rgba(20,184,166,0.85)" />
            <path d="M120 108v14l8 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>

        <div className="lg-taglines">
          <h2 className="lg-tagline-h">Track time. Ship work.</h2>
          <p className="lg-tagline-p">Lightweight time tracking for collaborative teams.</p>
        </div>

        <div className="lg-features">
          <div className="lg-feature-item">
            <div className="lg-feature-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
            </div>
            <div className="lg-feature-text">
              <span className="lg-feature-title">Multi-user time entries</span>
              <span className="lg-feature-desc">Anyone on the team can log time against any open task.</span>
            </div>
          </div>
          <div className="lg-feature-item">
            <div className="lg-feature-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
            </div>
            <div className="lg-feature-text">
              <span className="lg-feature-title">Tasks with status</span>
              <span className="lg-feature-desc">Open / Closed lifecycle, with concurrency-safe edits.</span>
            </div>
          </div>
          <div className="lg-feature-item">
            <div className="lg-feature-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><path d="M18 17V9" /><path d="M13 17V5" /><path d="M8 17v-3" /></svg>
            </div>
            <div className="lg-feature-text">
              <span className="lg-feature-title">Rolled-up reporting</span>
              <span className="lg-feature-desc">Per-task totals and per-user breakdowns out of the box.</span>
            </div>
          </div>
        </div>

        <div className="lg-brand-footer">
          <span className="lg-version">v0.1 · Local auth</span>
        </div>
      </aside>

      <main className="lg-form-panel">
        <div className="lg-form-inner">
          <div className="lg-app-identity">
            <div className="lg-app-badge">TT</div>
            <span className="lg-app-name">Time Tracker</span>
          </div>

          <h1 className="lg-form-h1">Sign in</h1>
          <p className="lg-form-sub">Local account · access your time-tracker workspace.</p>

          <form onSubmit={submit} noValidate>
            {error && (
              <div className="lg-error-alert">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                <span>{error}</span>
              </div>
            )}

            <div className="lg-field">
              <div className="lg-label-row"><label className="lg-label">Username</label></div>
              <div className="lg-input-wrap">
                <span className="lg-input-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                </span>
                <input
                  className="lg-input"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="admin"
                  autoComplete="username"
                  autoFocus
                />
              </div>
            </div>

            <div className="lg-field">
              <div className="lg-label-row"><label className="lg-label">Password</label></div>
              <div className="lg-input-wrap">
                <span className="lg-input-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                </span>
                <input
                  className="lg-input lg-input-pw"
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
                <button type="button" className="lg-pw-toggle" onClick={() => setShowPw(s => !s)} aria-label="toggle password visibility">
                  {showPw
                    ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                    : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>}
                </button>
              </div>
            </div>

            <button className="lg-submit" type="submit" disabled={isPending}>
              {isPending && <span className="lg-spinner" />}
              {isPending ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <div className="lg-form-footer">
            New here? <RouterLink to="/register">Create an account</RouterLink>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LocalLoginPage;
